import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ClearCellBody,
  FindConflictsParams,
  TimetableViewBy,
  UpsertCellBody,
  timetableApi,
  timetableViewsApi,
} from '../timetable';

export function timetableGridKey(classId: string) {
  return ['admin', 'timetable', 'grid', classId] as const;
}

export function useTimetableGridQuery(classId: string | undefined) {
  return useQuery({
    queryKey: timetableGridKey(classId ?? ''),
    queryFn: () => timetableApi.getGrid(classId!),
    enabled: !!classId,
  });
}

/** A student's own week. Disabled without an id, so a tab that does not show it never asks. */
export function useStudentTimetableQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'timetable', 'student', studentId ?? ''] as const,
    queryFn: () => timetableApi.getStudentTimetable(studentId!),
    enabled: !!studentId,
  });
}

export function useUpsertCellMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertCellBody) => timetableApi.upsertCell(body),
    onSuccess: (_data, body) => {
      void qc.invalidateQueries({ queryKey: timetableGridKey(body.classId) });
    },
  });
}

export function useClearCellMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClearCellBody) => timetableApi.clearCell(body),
    onSuccess: (_data, body) => {
      void qc.invalidateQueries({ queryKey: timetableGridKey(body.classId) });
    },
  });
}

export function useFindConflictsQuery(params: FindConflictsParams | null) {
  return useQuery({
    queryKey: ['admin', 'timetable', 'conflicts', params],
    queryFn: () => timetableApi.findConflicts(params!),
    enabled: !!params && (!!params.teacherId || !!params.roomId),
  });
}

/**
 * Naming no year is allowed: the API answers with the active one.
 *
 * It used to require a year, which broke the page for teachers — they cannot read
 * `/admin/school-years`, so they had none to name and the query never fired, leaving every
 * picker on the page empty and no timetable to look at.
 */
export function useTimetableViewsQuery(
  by: TimetableViewBy,
  schoolYearId: string | undefined,
  options?: { enabled?: boolean; teacherId?: string }
) {
  return useQuery({
    queryKey: ['admin', 'timetable', 'views', by, schoolYearId ?? 'active', options?.teacherId ?? 'all'] as const,
    queryFn: () => timetableViewsApi.getViews(by, schoolYearId, options?.teacherId),
    enabled: options?.enabled ?? true,
  });
}
